import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Button,
  Collapse,
  InputLabel,
  Menu,
  Slider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { capitalizeEachWord } from "../../../../../../utils/capitalize";
import { usePopover } from "../../../../../../utils/hooks/use-popover";
import { SET_DATA_TABLE_CELL_PADDING_COMMAND } from "../../../types/commands";

import type { CellPadding } from "../../../types/cell-padding";
import type { NodeKey } from "lexical";
import type { FC } from "react";

export const DataTablePadding: FC<{
  nodeKey: NodeKey;
  padding: CellPadding;
}> = ({ nodeKey, padding: initialPadding }) => {
  const [anchorRef, { handleToggle, handleClose, open }] = usePopover<HTMLButtonElement>();
  const [editor] = useLexicalComposerContext();
  const [padding, setPadding] = useState<CellPadding>(initialPadding);

  const handleSetIndividualPadding = useCallback(
    (setIndividually: boolean) => {
      const newPadding: CellPadding = setIndividually
        ? {
            setIndividually: true,
            top: !padding.setIndividually ? padding.all : 1,
            right: !padding.setIndividually ? padding.all : 1,
            bottom: !padding.setIndividually ? padding.all : 1,
            left: !padding.setIndividually ? padding.all : 1,
          }
        : {
            setIndividually: false,
            all: 1,
          };
      editor.dispatchCommand(SET_DATA_TABLE_CELL_PADDING_COMMAND, {
        nodeKey,
        padding: newPadding,
      });
      setPadding(newPadding);
    },
    [editor, nodeKey, padding, setPadding],
  );

  const handleCellPadding = useCallback(
    (
      value: number,
      position: "top" | "right" | "bottom" | "left" | "all",
      skipHistory?: boolean,
    ) => {
      const newPadding: CellPadding = {
        ...padding,
        [position]: value,
      };
      editor.dispatchCommand(SET_DATA_TABLE_CELL_PADDING_COMMAND, {
        nodeKey,
        padding: newPadding,
        skipHistory: skipHistory ?? false,
      });
      setPadding(newPadding);
    },
    [setPadding, padding, editor, nodeKey],
  );

  return (
    <>
      <Button
        data-analytics-id="lexical-table-menu-cell-padding"
        onClick={handleToggle}
        ref={anchorRef}
        endIcon={
          <KeyboardArrowDown
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        }
        sx={{ color: "text.secondary" }}
        size="small"
      >
        Cell padding
      </Button>
      <Menu
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { minWidth: 280 },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Stack direction="row" alignItems="center">
            <InputLabel id="padding-type-label">Edit individual paddings</InputLabel>
            <Switch
              checked={padding.setIndividually}
              onChange={(_, checked) => handleSetIndividualPadding(checked)}
              slotProps={{ input: { "aria-labelledby": "padding-type-label" } }}
            />
          </Stack>
          <Collapse in={padding.setIndividually} unmountOnExit>
            <DataTablePaddingSlider padding={padding} position="top" onChange={handleCellPadding} />
            <DataTablePaddingSlider
              padding={padding}
              position="right"
              onChange={handleCellPadding}
            />
            <DataTablePaddingSlider
              padding={padding}
              position="bottom"
              onChange={handleCellPadding}
            />
            <DataTablePaddingSlider
              padding={padding}
              position="left"
              onChange={handleCellPadding}
            />
          </Collapse>
          <Collapse in={!padding.setIndividually} unmountOnExit>
            <Stack direction="column" alignItems="flex-start" sx={{ px: 2 }}>
              {!padding.setIndividually && (
                <Slider
                  id="data-table-padding-slider"
                  onChange={(_, value) => handleCellPadding(value, "all", true)}
                  onChangeCommitted={(_, value) => handleCellPadding(value, "all", false)}
                  value={padding.all}
                  valueLabelFormat={(val) => `${val}px`}
                  valueLabelDisplay="auto"
                  aria-labelledby="data-table-padding-label"
                  max={50}
                  step={1}
                  sx={{ width: "100%" }}
                />
              )}
            </Stack>
          </Collapse>
        </Box>
      </Menu>
    </>
  );
};

const DataTablePaddingSlider: FC<{
  padding: CellPadding;
  position: "top" | "right" | "bottom" | "left";
  onChange: (
    value: number,
    position: "top" | "right" | "bottom" | "left",
    skipHistory?: boolean,
  ) => void;
}> = ({ padding, position, onChange }) => {
  if (!padding.setIndividually) return null;

  return (
    <>
      <Typography variant="body1" color="text.secondary">
        {capitalizeEachWord(position)}
      </Typography>
      <Stack direction="column" alignItems="flex-start" sx={{ px: 2 }}>
        <Slider
          id={`data-table-padding-${position}-slider`}
          onChange={(_, value) => onChange(value, position, true)}
          onChangeCommitted={(_, value) => onChange(value, position, false)}
          value={padding[position]}
          valueLabelFormat={(val) => `${val}px`}
          valueLabelDisplay="auto"
          max={50}
          step={1}
          sx={{ width: "100%" }}
        />
      </Stack>
    </>
  );
};
